// components/DominioPdfUpload.jsx
//
// Subida / visualización de los PDFs "Informe de dominio" (Características
// Generales → Localización → Titular de dominio). Una obra puede tener varios
// PDFs (varias parcelas). El input de subida se muestra SOLO para los roles
// admin, legal y admin_mejoramientos; el backend igual valida el rol.
//
// Los archivos se guardan en la base (bytea) en la tabla obras_dominio_pdf,
// vinculados por cg_01_id_partido_obra (en el front, id_partido_obra). Solo para
// obras guardadas (necesita el código).
//
// Ver/Descargar: el endpoint exige Authorization, así que se hace fetch con el
// token y se muestra el blob en un modal dentro de la app (no se abre pestaña).
import React, { useState, useEffect, useCallback } from 'react';
import { Label } from './ui';

const ROLES_GESTION = ['admin', 'legal', 'admin_mejoramientos'];
const MAX_BYTES = 10 * 1024 * 1024; // 10MB por archivo (mismo límite que multer)

// Estilos alineados con el resto del formulario (ui/Input, ui/Button).
const boxClass = 'w-full px-4 py-2 text-sm border border-gray-300 rounded-md bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-white';
const btnPrimary = 'px-3 py-1 rounded-md text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 transition-colors';
const btnDanger = 'px-3 py-1 rounded-md text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors';

const DominioPdfUpload = ({ codigoObra, token, role, roles }) => {
    const [metas, setMetas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [pdfUrl, setPdfUrl] = useState(null); // blob URL para el modal
    const [pdfNombre, setPdfNombre] = useState('');

    const rolesUsuario = Array.isArray(roles) && roles.length ? roles : (role ? [role] : []);
    const puedeGestionar = rolesUsuario.some((r) => ROLES_GESTION.includes(r));

    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const cargarLista = useCallback(async () => {
        if (!codigoObra || !token) return;
        setCargando(true);
        try {
            const res = await fetch(`/api/dominio-obra/${encodeURIComponent(codigoObra)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json().catch(() => ({}));
            const lista = data?.data;
            setMetas(Array.isArray(lista) ? lista : (lista ? [lista] : []));
        } catch (e) {
            console.error('[dominio pdf] no se pudo cargar la lista:', e);
        } finally {
            setCargando(false);
        }
    }, [codigoObra, token]);

    useEffect(() => {
        cargarLista();
    }, [cargarLista]);

    // Sin rol habilitado: no se muestra nada.
    if (!puedeGestionar) return null;

    if (!codigoObra) {
        return (
            <div className="mt-3">
                <Label>Informe de dominio (PDF)</Label>
                <p className="text-xs text-gray-500 dark:text-gray-400">Se habilita cuando la obra está guardada.</p>
            </div>
        );
    }

    const subir = async (files) => {
        const archivos = Array.from(files || []);
        if (!archivos.length) return;
        setError('');
        setMsg('');

        const noPdf = archivos.find((f) => f.type !== 'application/pdf');
        if (noPdf) {
            setError(`"${noPdf.name}" no es un PDF.`);
            return;
        }
        const grande = archivos.find((f) => f.size > MAX_BYTES);
        if (grande) {
            setError(`"${grande.name}" supera los 10MB.`);
            return;
        }

        setSubiendo(true);
        let subidos = 0;
        let ultimoError = '';
        for (const file of archivos) {
            try {
                const fd = new FormData();
                fd.append('archivo', file);
                const res = await fetch(`/api/dominio-obra/${encodeURIComponent(codigoObra)}`, {
                    method: 'POST',
                    headers,
                    body: fd,
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(data?.message || 'No se pudo subir el PDF');
                subidos += 1;
            } catch (e) {
                ultimoError = `${file.name}: ${e.message}`;
            }
        }
        await cargarLista();
        setSubiendo(false);
        if (subidos > 0) {
            setMsg(`${subidos} PDF${subidos === 1 ? '' : 's'} subido${subidos === 1 ? '' : 's'} correctamente.`);
        }
        if (ultimoError) setError(ultimoError);
    };

    const ver = async (id, nombre) => {
        setError('');
        try {
            const res = await fetch(`/api/dominio-obra/${encodeURIComponent(codigoObra)}/archivo/${id}`, { headers });
            if (!res.ok) throw new Error('No se pudo abrir el PDF');
            const blob = await res.blob();
            setPdfNombre(nombre || 'informe_dominio.pdf');
            setPdfUrl(URL.createObjectURL(blob));
        } catch (e) {
            setError(e.message);
        }
    };

    const cerrarPdf = () => {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setPdfNombre('');
    };

    const eliminar = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar "${nombre || 'este PDF'}"?`)) return;
        setError('');
        setMsg('');
        try {
            const res = await fetch(`/api/dominio-obra/${encodeURIComponent(codigoObra)}/${id}`, {
                method: 'DELETE',
                headers,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.message || 'No se pudo eliminar el PDF');
            await cargarLista();
            setMsg('PDF eliminado.');
        } catch (e) {
            setError(e.message);
        }
    };

    const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString('es-AR') : '');
    const peso = (n) => (n ? `${(n / 1024 / 1024).toFixed(2)} MB` : '');

    // Indicador de "subiendo": spinner + texto, para que se vea que el PDF se está
    // subiendo (el archivo puede tardar; sin esto parece que no pasó nada).
    const spinner = (
        <span className="inline-block w-3.5 h-3.5 mr-1.5 align-middle border-2 border-white/50 border-t-white rounded-full animate-spin" />
    );

    return (
        <div className="mt-3">
            <Label>Informe de dominio (PDF)</Label>

            {cargando && <p className="text-xs text-gray-500 dark:text-gray-400">Cargando…</p>}

            {!cargando && metas.length > 0 && (
                <div className="mt-1 space-y-2">
                    {metas.map((meta) => (
                        <div
                            key={meta.id}
                            className={`${boxClass} flex flex-wrap items-center justify-between gap-2`}
                        >
                            <span className="min-w-0 truncate text-gray-800 dark:text-gray-100">
                                {meta.nombreArchivo}
                                <span className="ml-2 text-xs text-gray-400">{peso(meta.tamanoBytes)}</span>
                                <span className="ml-2 text-xs text-gray-400">{fecha(meta.subidoEn)}</span>
                            </span>
                            <span className="flex shrink-0 gap-2">
                                <button
                                    type="button"
                                    onClick={() => ver(meta.id, meta.nombreArchivo)}
                                    className={btnPrimary}
                                >
                                    Ver
                                </button>
                                <button
                                    type="button"
                                    onClick={() => eliminar(meta.id, meta.nombreArchivo)}
                                    className={btnDanger}
                                >
                                    Eliminar
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {!cargando && metas.length === 0 && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No hay informes cargados.</p>
            )}

            <label className={`${btnPrimary} mt-2 inline-block ${subiendo ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                {subiendo ? <>{spinner}Subiendo…</> : 'Agregar PDF(s)'}
                <input
                    type="file"
                    accept="application/pdf"
                    multiple
                    className="hidden"
                    disabled={subiendo}
                    onChange={(e) => { subir(e.target.files); e.target.value = ''; }}
                />
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">PDF, hasta 10MB cada uno. Podés seleccionar varios.</p>

            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
            {msg && <p className="text-xs text-green-600 mt-1">{msg}</p>}

            {pdfUrl && (
                <div
                    className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
                    onClick={cerrarPdf}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[92vw] h-[92vh] max-w-6xl flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Informe de dominio</p>
                            <div className="flex items-center gap-2">
                                <a
                                    href={pdfUrl}
                                    download={pdfNombre || 'informe_dominio.pdf'}
                                    className={btnPrimary}
                                >
                                    Descargar
                                </a>
                                <button
                                    type="button"
                                    onClick={cerrarPdf}
                                    className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-xl leading-none"
                                    title="Cerrar"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                        <iframe src={pdfUrl} title="Informe de dominio" className="flex-1 w-full bg-white" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default DominioPdfUpload;
